// src/pages/financeiro/AssinaturaCad.js
import React, { useEffect, useState } from 'react';

import ModalContainer from '../../components/modalForm/ModalContainer';
import ModalHeader from '../../components/modalForm/ModalHeader';
import ModalBody from '../../components/modalForm/ModalBody';
import ModalFooter from '../../components/modalForm/ModalFooter';
import Input from '../../components/input/Input';
import Select from '../../components/input/Select';
import ClienteSearch from '../pessoas/ClienteSearch';
import AssinaturaSearch from './AssinaturaSearch';
import { useAssinatura } from '../../contexts/financeiro/AssinaturaContext';
import { useFilial } from '../../contexts/administrativo/FilialContext';
import { useMessage } from '../../contexts/geral/MessageContext';
import { useYesNo } from '../../contexts/geral/YesNoContext';
import { useFormHandler } from '../../hooks/useFormHandler';
import { Assinatura, statusClienteOptions } from '../../models/financeiro/Assinatura';
import { parseCurrency } from '../../utils/numberUtils';
import ModalActions, { ModalActionButton } from '../../components/modalForm/ModalActions';

const AssinaturaCad = ({ show, onHide }) => {
  const { cobrancas, addCobranca, updateCobranca, deleteCobranca } = useAssinatura();
  const { filialLogin } = useFilial();
  const { showMessage } = useMessage();
  const { showYesNo } = useYesNo();
  const { isSubmitting, handleSubmit } = useFormHandler();
  const [cobranca, setCobranca] = useState(new Assinatura({ filialId: filialLogin?.id || null }));
  const [clienteNome, setClienteNome] = useState('');
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [showSearchModalCliente, setShowSearchModalCliente] = useState(false);
  const [novoReg, setNovoReg] = useState(true);

  useEffect(() => {
    if (show) {
      handleNovo();
    }
  }, [show]);

  const handleNovo = () => {
    setCobranca(new Assinatura({ filialId: filialLogin?.id || null }));
    setClienteNome('');
    setNovoReg(true);
  };

  const handleEditar = () => {
    setNovoReg(false);
  };

  const handleExit = () => {
    const cobrancaSel = cobrancas.find((item) => item.id?.toString() === cobranca.id?.toString());

    if (cobrancaSel) {
      handleSelectCobranca(cobrancaSel);
    } else {
      handleNovo();
    }
  };

  const handleSelectCobranca = (cobrancaSel) => {
    if (!cobrancaSel) {
      handleNovo();
      return;
    }

    setCobranca(new Assinatura({
      ...cobrancaSel,
      horarioCobranca: cobrancaSel.horarioCobranca?.substring(0, 8) || '09:00:00',
    }));
    setClienteNome(cobrancaSel.cliente?.pessoa?.nome || '');
    setShowSearchModal(false);
    handleEditar();
  };

  const handleSelectCliente = (clienteSel) => {
    setShowSearchModalCliente(false);
    if (!clienteSel) return;

    setClienteNome(clienteSel.pessoa?.nome || '');
    setCobranca((prev) => ({
      ...prev,
      clienteId: clienteSel.id,
    }));
  };

  const handleSave = () => {
    if (!cobranca.clienteId) {
      showMessage('Informe o cliente da assinatura.');
      return;
    }

    if (Number(cobranca.valor || 0) < 0) {
      showMessage('O valor da assinatura não pode ser negativo.');
      return;
    }

    handleSubmit(
      async () => {
        const payload = {
          ...cobranca,
          valor: Number(cobranca.valor || 0),
          diaVencimento: Number(cobranca.diaVencimento),
          statusCliente: Number(cobranca.statusCliente),
          filialId: cobranca.filialId || filialLogin?.id || null,
          horarioCobranca: cobranca.horarioCobranca?.length === 5 ? `${cobranca.horarioCobranca}:00` : cobranca.horarioCobranca,
        };

        if (!payload.id) {
          await addCobranca(payload);
        } else {
          await updateCobranca(payload);
        }

        handleNovo();
      },
      () => {},
      (error) => console.error('Erro ao gravar assinatura: ', error),
    );
  };

  const handleDelete = async () => {
    if (!cobranca?.id) {
      showMessage('Selecione uma assinatura para excluir!');
      return;
    }

    const resposta = await showYesNo('Deseja excluir a assinatura selecionada?');
    if (resposta) {
      await deleteCobranca(cobranca.id);
      handleNovo();
    }
  };

  if (!show) return null;

  return (
    <ModalContainer show={show} variant="cadastro" onHide={onHide}>
      <ModalHeader title={novoReg ? 'Nova Assinatura' : 'Alterar Assinatura'} onHide={onHide} />

      <ModalBody>
        <form>
          <div className="input-group mb-3">
            <Input
              label="Código"
              value={cobranca.id || ''}
              onChange={(e) => setCobranca({ ...cobranca, id: e.target.value })}
              onBlur={handleExit}
              disabled={!novoReg}
              btnBuscar={() => setShowSearchModal(true)}
            />
            <Input
              label="Cliente"
              value={clienteNome}
              disabled
              size={260}
              btnBuscar={() => setShowSearchModalCliente(true)}
            />
            <Input
              label="Valor"
              value={cobranca.valor || ''}
              size={120}
              onChange={(e) => setCobranca({ ...cobranca, valor: parseCurrency(e.target.value) })}
            />
            <Input
              label="Dia"
              type="number"
              value={cobranca.diaVencimento}
              size={80}
              onChange={(e) => setCobranca({ ...cobranca, diaVencimento: e.target.value })}
            />
            <Input
              label="Horário"
              type="time"
              value={cobranca.horarioCobranca?.substring(0, 5) || '09:00'}
              size={110}
              onChange={(e) => setCobranca({ ...cobranca, horarioCobranca: `${e.target.value}:00` })}
            />
            <Select
              label="Status"
              value={cobranca.statusCliente}
              options={statusClienteOptions}
              size={130}
              onChange={(e) => setCobranca({ ...cobranca, statusCliente: Number(e.target.value) })}
            />
            <Input
              label="Descrição"
              value={cobranca.descricao || ''}
              size={300}
              onChange={(e) => setCobranca({ ...cobranca, descricao: e.target.value })}
              autoFocus
            />
          </div>
        </form>
      </ModalBody>

      <ModalFooter>
        <ModalActions >
          <ModalActionButton type="button" className="btn btn-primary" onClick={handleSave} disabled={isSubmitting} action="save">
            {isSubmitting ? 'Salvando...' : 'Salvar'}
          </ModalActionButton>
          <ModalActionButton type="button" className="btn btn-danger" onClick={handleDelete} disabled={novoReg} action="delete">
            Excluir
          </ModalActionButton>
          <ModalActionButton type="button" className="btn btn-secondary" onClick={handleNovo} disabled={novoReg} action="new">
            Novo
          </ModalActionButton>
        </ModalActions>
      </ModalFooter>

      <AssinaturaSearch
        show={showSearchModal}
        onHide={() => setShowSearchModal(false)}
        onSelectCobranca={handleSelectCobranca}
      />
      <ClienteSearch
        show={showSearchModalCliente}
        onHide={() => setShowSearchModalCliente(false)}
        onSelectCliente={handleSelectCliente}
      />
    </ModalContainer>
  );
};

export default AssinaturaCad;
